import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarClock, Loader2, Star, X, CreditCard, Check, Clock, Search, GitCompare, Plane, Ticket, Download, MapPin, Share2, Car } from "lucide-react";
import BookingsCalendar from "@/components/BookingsCalendar";
import { LiveMap } from "@/components/LiveMap";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";

const STATUS = {
  quote_requested: { label: "Awaiting quote", cls: "bg-slate-100 text-slate-600" },
  quote_sent: { label: "Quote ready", cls: "bg-violet-100 text-violet-700" },
  pending_approval: { label: "Awaiting provider", cls: "bg-slate-100 text-slate-600" },
  awaiting_payment: { label: "Awaiting payment", cls: "bg-amber-100 text-amber-700" },
  confirmed: { label: "Confirmed", cls: "bg-emerald-100 text-emerald-700" },
  completed: { label: "Completed", cls: "bg-blue-100 text-blue-700" },
  cancelled: { label: "Cancelled", cls: "bg-rose-100 text-rose-700" },
  declined: { label: "Declined", cls: "bg-rose-100 text-rose-700" },
};

const TABS = [["all", "All"], ["pending", "Pending"], ["confirmed", "Confirmed"], ["completed", "Completed"], ["cancelled", "Cancelled"]];
const TAB_MATCH = {
  pending: ["quote_requested", "quote_sent", "pending_approval", "awaiting_payment"],
  confirmed: ["confirmed"],
  completed: ["completed"],
  cancelled: ["cancelled", "declined"],
};

const Pill = ({ b }) => { const st = STATUS[b.status] || STATUS.confirmed; return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${st.cls}`} data-testid={`booking-status-${b.booking_id}`}>{st.label}</span>; };

const isExpired = (b) => b.quote_expired || (b.quote_expires_at && new Date(b.quote_expires_at) < new Date());
const seatSummary = (b) => {
  const sd = b.seats_drawn; if (!sd) return null;
  const total = (sd.a || 0) + (sd.c || 0) + (sd.i || 0); if (!total) return null;
  const parts = [];
  if (sd.a) parts.push(`${sd.a} adult${sd.a > 1 ? "s" : ""}`);
  if (sd.c) parts.push(`${sd.c} child${sd.c > 1 ? "ren" : ""}`);
  if (sd.i) parts.push(`${sd.i} infant${sd.i > 1 ? "s" : ""}`);
  return `${parts.join(" · ")} · ${total} seat${total > 1 ? "s" : ""}`;
};

function Countdown({ iso }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const ms = new Date(iso).getTime() - now;
  if (ms <= 0) return <span className="font-semibold text-rose-500">Expired</span>;
  const s = Math.floor(ms / 1000), d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const txt = d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m ${sec}s`;
  return <span>expires in {txt}</span>;
}

const RATING_FILTERS = [[0, "Any rating"], [3, "3★+"], [4, "4★+"], [4.5, "4.5★+"]];

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true); s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function StarRow({ value, onPick, readonly }) {
  return (
    <div className="flex items-center gap-0.5" data-testid="driver-star-row">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={readonly} onClick={() => onPick && onPick(n)} data-testid={`driver-star-${n}`} className={readonly ? "" : "cursor-pointer"}>
          <Star className={`h-5 w-5 ${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
        </button>
      ))}
    </div>
  );
}

// Gentle two-note chime + a short vibration. Best-effort: browsers block audio until a user
// gesture (opening the ride card counts), and true screen-off delivery would need web-push.
function playArrivalCue() {
  try { if (navigator.vibrate) navigator.vibrate([180, 90, 180]); } catch { /* ignore */ }
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [[880, 0], [1174.66, 0.18]].forEach(([f, t]) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = f; o.connect(g); g.connect(ctx.destination);
      const s = ctx.currentTime + t;
      g.gain.setValueAtTime(0.0001, s);
      g.gain.exponentialRampToValueAtTime(0.22, s + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, s + 0.4);
      o.start(s); o.stop(s + 0.45);
    });
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 1300);
  } catch { /* audio blocked until a user gesture */ }
}

// Stronger 3-note rising cue + longer vibration when waiting charges cross a round figure.
function playMilestoneCue() {
  try { if (navigator.vibrate) navigator.vibrate([260, 120, 260, 120, 320]); } catch { /* ignore */ }
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [[659.25, 0], [783.99, 0.16], [1046.5, 0.32]].forEach(([f, t]) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "triangle"; o.frequency.value = f; o.connect(g); g.connect(ctx.destination);
      const s = ctx.currentTime + t;
      g.gain.setValueAtTime(0.0001, s);
      g.gain.exponentialRampToValueAtTime(0.3, s + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, s + 0.42);
      o.start(s); o.stop(s + 0.46);
    });
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 1500);
  } catch { /* audio blocked until a user gesture */ }
}

const WAIT_MILESTONE_STEP = 50;

// Amber waiting banner with a live-ticking accrued charge (updates every second).
function WaitingCostBanner({ detail, onDismiss }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const w = (detail.ride_mobility && detail.ride_mobility.waiting) || {};
  const rate = Number(w.rate || 0);
  const unit = w.rate_unit || "min";
  const perMin = /h/i.test(unit) ? rate / 60 : rate;
  const grace = Number(w.grace_minutes != null ? w.grace_minutes : (w.free_minutes || 0));
  const startedMs = detail.waiting_started_at ? new Date(detail.waiting_started_at).getTime() : now;
  const meteredSec = Math.max(0, Math.floor((now - startedMs) / 1000) - grace * 60);
  const mm = Math.floor(meteredSec / 60); const ss = meteredSec % 60;
  const cost = Math.round((meteredSec / 60) * perMin);
  // Stronger cue each time the charge crosses a ₹50 / ₹100 / ₹150 … round figure.
  const lastMilestoneRef = useRef(0);
  const [milestone, setMilestone] = useState(0);
  useEffect(() => {
    const crossed = Math.floor(cost / WAIT_MILESTONE_STEP) * WAIT_MILESTONE_STEP;
    if (crossed >= WAIT_MILESTONE_STEP && crossed > lastMilestoneRef.current) {
      lastMilestoneRef.current = crossed;
      playMilestoneCue();
      setMilestone(crossed);
      const t = setTimeout(() => setMilestone(0), 4000);
      return () => clearTimeout(t);
    }
  }, [cost]);
  const hot = milestone > 0;
  return (
    <div data-testid="cb-waiting-banner" className={`mb-2 flex items-start gap-2 rounded-xl border px-3 py-2 transition-all ${hot ? "border-rose-400 bg-rose-50 ring-2 ring-rose-300" : "border-amber-300 bg-amber-50"}`}>
      <Clock className={`mt-0.5 h-4 w-4 shrink-0 ${hot ? "text-rose-600" : "text-amber-600"}`} />
      <div className="flex-1">
        <p className={`text-[12px] font-semibold leading-snug ${hot ? "text-rose-800" : "text-amber-800"}`}>Free wait time is over — waiting charges have started. Please reach the pickup point.</p>
        <p className={`mt-0.5 text-[12px] font-bold ${hot ? "text-rose-900" : "text-amber-900"}`}>
          Charges so far: <span data-testid="cb-waiting-cost">₹{cost}</span>
          <span className={`ml-1 font-mono text-[11px] font-semibold ${hot ? "text-rose-700" : "text-amber-700"}`} data-testid="cb-waiting-timer">({mm}:{String(ss).padStart(2, "0")} @ ₹{rate}/{unit})</span>
        </p>
        {hot && <p data-testid="cb-waiting-milestone" className="mt-1 inline-block animate-pulse rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-bold text-white">Waiting charges just passed ₹{milestone}</p>}
      </div>
      <button onClick={onDismiss} data-testid="cb-waiting-banner-dismiss" className={`shrink-0 rounded-full p-0.5 ${hot ? "text-rose-500 hover:bg-rose-100" : "text-amber-500 hover:bg-amber-100"}`}><X className="h-3.5 w-3.5" /></button>
    </div>
  );
}

function RideActions({ detail, onRated }) {
  const [rating, setRating] = useState(detail.driver_rating || 0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const receipt = async () => {
    try { await downloadFile(`/services/booking/${detail.booking_id}/ride-receipt`, `ride-receipt-${detail.booking_id.slice(0, 8)}.pdf`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't download the receipt."); }
  };
  const submit = async () => {
    if (!rating) { toast.error("Tap the stars to rate."); return; }
    setBusy(true);
    try { await api.post(`/services/booking/${detail.booking_id}/rate-driver`, { rating, comment }); toast.success("Thanks for rating your driver!"); onRated && onRated(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="cb-ride-actions">
      <p className="text-sm font-bold text-emerald-700" data-testid="cb-ride-fare">Trip complete · Fare ₹{detail.final_fare} ({detail.distance_km} km) — pay the driver in cash/UPI.</p>
      <button onClick={receipt} data-testid="cb-ride-receipt" className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Download className="h-3.5 w-3.5" /> Download receipt</button>
      <div className="mt-3 rounded-xl bg-white p-3" data-testid="cb-driver-rating">
        {detail.driver_rating ? (
          <div className="flex items-center gap-2"><span className="text-xs font-semibold text-slate-500">You rated {detail.driver_name}:</span><StarRow value={detail.driver_rating} readonly /></div>
        ) : (
          <>
            <p className="text-xs font-semibold text-slate-600">Rate your driver {detail.driver_name}</p>
            <div className="mt-1"><StarRow value={rating} onPick={setRating} /></div>
            <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Optional comment" data-testid="cb-rating-comment" className="mt-2 h-8 w-full rounded-md border border-slate-200 px-2 text-sm outline-none focus:border-sky-400" />
            <button onClick={submit} disabled={busy} data-testid="cb-rating-submit" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Star className="h-3.5 w-3.5" />} Submit rating</button>
          </>
        )}
      </div>
    </div>
  );
}

export default function CustomerBookings() {
  const [bookings, setBookings] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState("");
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [view, setView] = useState("list");
  const [detail, setDetail] = useState(null);
  const [compare, setCompare] = useState(null); // request_id
  const [sp] = useSearchParams();
  const [compareSort, setCompareSort] = useState("price"); // price | rating
  const [compareMinRating, setCompareMinRating] = useState(0);
  const [askText, setAskText] = useState({});
  const [nudgeDismissed, setNudgeDismissed] = useState(false);
  // Ride alert banners (driver-arrived / waiting-charges) — dismissals persist so they don't nag on poll refresh.
  const [dismissedAlerts, setDismissedAlerts] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("rideAlertsDismissed") || "[]")); } catch { return new Set(); }
  });
  const dismissAlert = (key) => setDismissedAlerts((s) => {
    const n = new Set(s); n.add(key);
    try { localStorage.setItem("rideAlertsDismissed", JSON.stringify([...n])); } catch { /* ignore */ }
    return n;
  });
  const chimedRef = useRef(new Set());

  const shareTrip = async (bid) => {
    try {
      const r = await api.post(`/services/booking/${bid}/share`);
      let url = `${window.location.origin}/track/${r.data.token}`;
      try { const s = await api.post("/links/shorten", { url }); if (s.data?.url) url = s.data.url; } catch { /* keep long link */ }
      if (navigator.share) { try { await navigator.share({ title: "Track my ride", text: "Follow my live trip", url }); } catch { /* user cancelled */ } }
      else { await navigator.clipboard.writeText(url); toast.success("Trip link copied — share it with family"); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const askQuote = async (b) => {
    const q = (askText[b.booking_id] || "").trim();
    if (!q) { toast.error("Type your question first."); return; }
    try { await api.post(`/services/booking/${b.booking_id}/ask`, { question: q }); toast.success("Question sent to the provider"); setAskText((s) => ({ ...s, [b.booking_id]: "" })); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const [seenSet] = useState(() => new Set());
  const thread = (b) => b.qa_thread && b.qa_thread.length ? b.qa_thread : ((b.qa?.question) ? [{ from: "customer", text: b.qa.question, at: b.qa.question_at }, ...(b.qa.answer ? [{ from: "provider", text: b.qa.answer, at: b.qa.answer_at }] : [])] : []);
  const custMsgCount = (b) => thread(b).filter((m) => m.from === "customer").length;
  const unreadFromProvider = (b) => thread(b).filter((m) => m.from === "provider" && (!b.qa_seen_customer || m.at > b.qa_seen_customer)).length;
  const markQaSeen = (b) => { if (seenSet.has(b.booking_id) || unreadFromProvider(b) === 0) return; seenSet.add(b.booking_id); api.post(`/services/booking/${b.booking_id}/qa-seen`).catch(() => {}); };

  const load = useCallback(() => { api.get("/services/my-bookings").then((r) => setBookings(r.data.bookings || [])).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, []);
  useEffect(() => { load(); }, [load]);
  // Live-refresh the OPEN ride detail so arrival/waiting banners appear without reopening the modal.
  useEffect(() => {
    if (!detail?.live_ride || !["awaiting_assignment", "assigned", "in_progress"].includes(detail.ride_status)) return;
    const id = detail.booking_id;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/services/booking/${id}`);
        if (r.data?.booking) setDetail((d) => (d && d.booking_id === id ? { ...d, ...r.data.booking } : d));
      } catch { /* transient — keep polling */ }
    }, 8000);
    return () => clearInterval(t);
  }, [detail?.booking_id, detail?.live_ride, detail?.ride_status]);
  // Chime + vibrate once, the moment the driver-arrived banner first appears for a booking.
  useEffect(() => {
    if (!detail || !detail.arrived_at || detail.ride_status !== "assigned") return;
    if (chimedRef.current.has(detail.booking_id)) return;
    chimedRef.current.add(detail.booking_id);
    playArrivalCue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.booking_id, detail?.arrived_at, detail?.ride_status]);
  // Deep-link from a "new quote" nudge: /app/bookings?compare=<request_id> opens the compare view.
  useEffect(() => {
    const rid = sp.get("compare");
    if (rid && bookings && bookings.some((b) => b.request_id === rid)) setCompare(rid);
  }, [bookings, sp]);

  const cancel = async (b) => { if (!window.confirm("Cancel this booking?")) return; try { await api.post(`/services/booking/${b.booking_id}/cancel`); toast.success("Cancelled"); setDetail(null); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const downloadEticket = async (b) => { try { await downloadFile(`/services/booking/${b.booking_id}/eticket`, `eticket-${b.booking_id.slice(0, 8)}.pdf`); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't download the e-ticket."); } };
  const submitReview = async () => { try { await api.post("/services/review", { booking_id: reviewing.booking_id, rating, comment }); toast.success("Thanks for your review!"); setReviewing(null); setComment(""); setRating(5); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const payNow = async (b) => {
    setBusy(b.booking_id);
    try {
      const r = await api.post(`/services/booking/${b.booking_id}/pay`);
      const pay = r.data.payment;
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Couldn't load the payment window."); setBusy(""); return; }
      const rzp = new window.Razorpay({
        key: pay.key_id, order_id: pay.order_id, amount: pay.amount, currency: pay.currency,
        name: "Suffle Services", description: b.service_name,
        handler: async (resp) => {
          try { await api.post("/services/book/verify-payment", { booking_id: b.booking_id, ...resp }); toast.success("Payment confirmed — booking is set!"); load(); }
          catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        },
      });
      rzp.on("payment.failed", (x) => toast.error("Payment failed", { description: x?.error?.description }));
      rzp.open(); setBusy("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(""); }
  };

  const payInstalment = async (b, idx) => {
    setBusy(b.booking_id);
    try {
      const r = await api.post(`/services/booking/${b.booking_id}/instalment/${idx}/pay`);
      const pay = r.data.payment;
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Couldn't load the payment window."); setBusy(""); return; }
      const rzp = new window.Razorpay({
        key: pay.key_id, order_id: pay.order_id, amount: pay.amount, currency: pay.currency,
        name: "Suffle Holidays", description: `${b.service_name} — instalment`,
        handler: async (resp) => {
          try { await api.post(`/services/booking/${b.booking_id}/instalment/verify`, { booking_id: b.booking_id, ...resp }); toast.success("Instalment paid — thank you!"); load(); }
          catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        },
      });
      rzp.on("payment.failed", (x) => toast.error("Payment failed", { description: x?.error?.description }));
      rzp.open(); setBusy("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(""); }
  };

  const acceptQuote = async (b) => {
    setBusy(b.booking_id);
    try {
      const r = await api.post(`/services/booking/${b.booking_id}/accept-quote`);
      setDetail(null); setCompare(null);
      if (r.data.payment?.required) { setBusy(""); await payNow(b); }
      else { toast.success("Quote accepted — booking confirmed!"); load(); setBusy(""); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(""); }
  };

  const actions = (b) => (
    <div className="flex flex-wrap items-center gap-2">
      {b.status === "quote_sent" && <button onClick={() => acceptQuote(b)} disabled={busy === b.booking_id} data-testid={`accept-quote-${b.booking_id}`} className="inline-flex items-center gap-1 rounded-full bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"><Check className="h-3.5 w-3.5" /> Accept ₹{b.amount}</button>}
      {b.status === "awaiting_payment" && <button onClick={() => payNow(b)} disabled={busy === b.booking_id} data-testid={`pay-${b.booking_id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"><CreditCard className="h-3.5 w-3.5" /> Pay ₹{b.pay_amount || b.amount}</button>}
      {b.status === "completed" && <button onClick={() => { setDetail(null); setReviewing(b); }} data-testid={`review-${b.booking_id}`} className="inline-flex items-center gap-1 rounded-full border border-amber-200 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-50"><Star className="h-3.5 w-3.5" /> Review</button>}
      {["confirmed", "awaiting_payment", "pending_approval", "quote_requested", "quote_sent"].includes(b.status) && <button onClick={() => cancel(b)} data-testid={`cancel-${b.booking_id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><X className="h-3.5 w-3.5" /> Cancel</button>}
    </div>
  );

  if (bookings === null) return <div className="flex h-64 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const compareItems = compare ? bookings.filter((b) => b.request_id === compare) : [];

  return (
    <div data-testid="customer-bookings">
      <h1 className="mb-1 flex items-center gap-2 text-2xl font-bold text-slate-900"><CalendarClock className="h-6 w-6 text-fuchsia-500" /> My Bookings</h1>
      <p className="mb-4 text-sm text-slate-500">Your service bookings and appointments.</p>
      {(() => {
        const n = (bookings || []).find((b) => b.live_ride && b.ride_status === "completed" && !b.driver_rating);
        if (!n || nudgeDismissed) return null;
        return (
          <div data-testid="cb-rate-nudge" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm text-amber-800"><Star className="mr-1 inline h-4 w-4 fill-amber-400 text-amber-400" /> Your trip with <b>{n.driver_name || "your driver"}</b> is complete — how was it?</p>
            <div className="flex items-center gap-2">
              <button onClick={() => setDetail(n)} data-testid="cb-rate-nudge-go" className="rounded-full bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white">Rate now</button>
              <button onClick={() => setNudgeDismissed(true)} data-testid="cb-rate-nudge-dismiss" className="rounded-full border border-amber-300 px-2.5 py-1.5 text-xs font-semibold text-amber-700">Later</button>
            </div>
          </div>
        );
      })()}
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="bookings-tabs">
        {TABS.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`bookings-tab-${id}`}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${tab === id ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{label}</button>
        ))}
        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="bookings-search" placeholder="Search bookings..." className="h-8 w-48 rounded-full border border-slate-200 pl-8 pr-3 text-xs outline-none focus:border-fuchsia-400" />
        </div>
        <div className="flex rounded-full border border-slate-200 p-0.5" data-testid="cb-view-toggle">
          {[["list", "List"], ["calendar", "Calendar"]].map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} data-testid={`cb-view-${v}`} className={`rounded-full px-3 py-1 text-xs font-semibold ${view === v ? "bg-slate-900 text-white" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
      </div>
      {view === "calendar" && <BookingsCalendar bookings={bookings} onItemClick={setDetail} />}
      {view === "list" && (() => {
        const ql = query.trim().toLowerCase();
        const base = (tab === "all" ? bookings : bookings.filter((b) => (TAB_MATCH[tab] || []).includes(b.status)))
          .filter((b) => !ql || (b.service_name || "").toLowerCase().includes(ql) || (b.request_summary || "").toLowerCase().includes(ql));
        // Collapse broadcast travel requests (same request_id) into a single comparison row.
        const groupsMap = {};
        bookings.forEach((b) => { if (b.broadcast && b.request_id) (groupsMap[b.request_id] = groupsMap[b.request_id] || []).push(b); });
        const entries = []; const doneGroups = new Set();
        base.forEach((b) => {
          if (b.broadcast && b.request_id) {
            if (doneGroups.has(b.request_id)) return;
            doneGroups.add(b.request_id);
            entries.push({ type: "group", request_id: b.request_id, items: groupsMap[b.request_id] || [b] });
          } else entries.push({ type: "single", b });
        });
        return (<>
      {entries.length === 0 && <p className="rounded-xl border border-slate-100 p-10 text-center text-sm text-slate-400" data-testid="bookings-empty">{tab === "all" ? "No bookings yet. Book a service from the marketplace." : "No bookings in this group."}</p>}
      <div className="space-y-3">
        {entries.map((e) => {
          if (e.type === "group") {
            const items = e.items;
            const head = items[0];
            const quotes = items.filter((x) => x.status === "quote_sent" && !isExpired(x));
            const accepted = items.find((x) => ["confirmed", "completed", "awaiting_payment"].includes(x.status));
            const amounts = quotes.map((x) => Number(x.amount)).filter((n) => n > 0);
            const low = amounts.length ? Math.min(...amounts) : null;
            const high = amounts.length ? Math.max(...amounts) : null;
            return (
            <div key={e.request_id} className="rounded-2xl border border-sky-200 bg-white p-4" data-testid={`booking-group-${e.request_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div onClick={() => quotes.length > 0 && !accepted && setCompare(e.request_id)} className={quotes.length > 0 && !accepted ? "cursor-pointer" : ""} data-testid={`booking-group-open-${e.request_id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="inline-flex items-center gap-1.5 font-semibold text-slate-800"><Plane className="h-4 w-4 text-sky-500" /> {head.travel_request?.subcategory_label || "Travel request"}</p>
                    <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700">Sent to {items.length} agent{items.length > 1 ? "s" : ""}</span>
                    {accepted ? <Pill b={accepted} /> : <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700">{quotes.length} quote{quotes.length !== 1 ? "s" : ""} received</span>}
                  </div>
                  {head.request_summary && <p className="mt-1 text-xs text-slate-500">{head.request_summary}</p>}
                  {low != null && !accepted && <p className="mt-1 text-xs font-semibold text-emerald-600">Quotes from ₹{low}{high !== low ? ` – ₹${high}` : ""}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {quotes.length > 0 && !accepted && <button onClick={() => setCompare(e.request_id)} data-testid={`compare-quotes-${e.request_id}`} className="inline-flex items-center gap-1 rounded-full bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-700"><GitCompare className="h-3.5 w-3.5" /> Compare quotes ({quotes.length})</button>}
                  {accepted && actions(accepted)}
                </div>
              </div>
            </div>
          ); }
          const b = e.b;
          return (
          <div key={b.booking_id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4" data-testid={`booking-${b.booking_id}`}>
            <div onClick={() => setDetail(b)} className="min-w-0 flex-1 cursor-pointer" data-testid={`booking-open-${b.booking_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-semibold text-slate-800">{b.service_name}</p>
                <Pill b={b} />
                {b.live_ride && ["assigned", "in_progress"].includes(b.ride_status) && <span data-testid={`cb-track-badge-${b.booking_id}`} className="inline-flex items-center gap-1 rounded-full bg-sky-600 px-2 py-0.5 text-[10px] font-semibold text-white animate-pulse"><MapPin className="h-3 w-3" /> Track your driver</span>}
                {b.live_ride && ["assigned", "in_progress"].includes(b.ride_status) && <button onClick={(e) => { e.stopPropagation(); shareTrip(b.booking_id); }} data-testid={`cb-share-row-${b.booking_id}`} className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-slate-800"><Share2 className="h-3 w-3" /> Share trip</button>}
                {b.ticket?.pnr && <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-700" data-testid={`cb-pnr-chip-${b.booking_id}`}><Ticket className="h-3 w-3" /> PNR {b.ticket.pnr}</span>}
                {b.reference?.ref_number && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`cb-ref-chip-${b.booking_id}`}><Ticket className="h-3 w-3" /> {b.reference.ref_type} {b.reference.ref_number}</span>}
              </div>
              <p className="mt-1 text-xs text-slate-400">{b.slot_label || "Instant"}{b.amount ? ` · ₹${b.amount}` : ""}{b.payment_status && b.payment_status !== "none" ? ` · ${b.payment_status}` : ""}</p>
              {seatSummary(b) && <p className="mt-1 text-[11px] font-medium text-slate-500" data-testid={`cb-seats-${b.booking_id}`}>🎫 {seatSummary(b)}</p>}
              {b.request_summary && <p className="mt-1 rounded-lg bg-sky-50 px-2 py-1 text-[11px] font-medium text-sky-700" data-testid={`cb-travel-${b.booking_id}`}>{b.travel_request?.subcategory_label || "Travel"}: {b.request_summary}</p>}
              {b.status === "quote_sent" && b.quote_note && <p className="mt-1 text-xs text-violet-600">Provider note: {b.quote_note}</p>}
              {b.status === "quote_sent" && b.quote_flight && (
                <div className="mt-1 rounded-lg border border-sky-100 bg-sky-50 px-2 py-1.5 text-[11px] text-sky-800" data-testid={`cb-flight-quote-${b.booking_id}`}>
                  <p className="font-bold">{b.quote_flight.airline || "Flight"} {b.quote_flight.flight_no || ""}</p>
                  <p>{[b.quote_flight.dep_date, b.quote_flight.dep_time].filter(Boolean).join(" ")} → {[b.quote_flight.arr_date, b.quote_flight.arr_time].filter(Boolean).join(" ")}</p>
                  <p>{["fare_a", "fare_c", "fare_i"].map((k, ix) => b.quote_flight[k] ? `${["Adult", "Child", "Infant"][ix]} ₹${b.quote_flight[k]}` : null).filter(Boolean).join(" · ") || "Fare on request"}{b.quote_flight.baggage ? ` · ${b.quote_flight.baggage}` : ""}</p>
                </div>
              )}
              {b.status === "quote_sent" && (b.revision_changes || []).length > 0 && (
                <div className="mt-1 rounded-lg border border-violet-100 bg-violet-50 px-2 py-1.5" data-testid={`cb-revised-${b.booking_id}`}>
                  <p className="text-[11px] font-bold text-violet-800">Revised plan — what changed</p>
                  {(b.revision_changes || []).slice(0, 6).map((c, i) => <p key={i} className="text-[11px] text-violet-700">• {c}</p>)}
                </div>
              )}
              {(b.instalments || []).length > 0 && (
                <div className="mt-1.5 rounded-lg border border-slate-100 p-2 text-[11px]" data-testid={`cb-instalments-${b.booking_id}`}>
                  <p className="font-semibold text-slate-700">Payment plan{b.travel_date ? ` · travel ${b.travel_date}` : ""}</p>
                  {b.instalments.map((ins) => (
                    <div key={ins.i} className="mt-0.5 flex items-center justify-between gap-2">
                      <span className="text-slate-600">{ins.label} · ₹{ins.amount}{ins.due_date ? ` · due ${ins.due_date}` : ""}</span>
                      {ins.paid
                        ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">Paid</span>
                        : <button onClick={() => payInstalment(b, ins.i)} disabled={busy === b.booking_id} data-testid={`cb-instalment-pay-${b.booking_id}-${ins.i}`} className="rounded-full bg-fuchsia-600 px-2.5 py-0.5 font-semibold text-white disabled:opacity-50 hover:bg-fuchsia-700">Pay now</button>}
                    </div>
                  ))}
                </div>
              )}
              {b.status === "quote_requested" && <p className="mt-1 inline-flex items-center gap-1 text-xs text-slate-400"><Clock className="h-3 w-3" /> Waiting for the provider to send a price.</p>}
              {b.status === "pending_approval" && <p className="mt-1 inline-flex items-center gap-1 text-xs text-slate-400"><Clock className="h-3 w-3" /> Waiting for the provider to accept.</p>}
            </div>
            {actions(b)}
          </div>
        ); })}
      </div>
        </>);
      })()}

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDetail(null)} data-testid="booking-detail-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-5" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-semibold text-slate-800">{detail.service_name}</h3>
                <div className="mt-1"><Pill b={detail} /></div>
              </div>
              <button onClick={() => setDetail(null)} data-testid="booking-detail-close" className="rounded-full p-1 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 space-y-1.5 text-sm text-slate-600">
              <p><span className="text-slate-400">When:</span> {detail.slot_label || "Instant / flexible"}</p>
              {seatSummary(detail) && <p><span className="text-slate-400">Passengers:</span> {seatSummary(detail)}</p>}
              {detail.ticket?.pnr && <p data-testid="cb-ticket-detail"><span className="text-slate-400">Ticket:</span> PNR <span className="font-semibold text-sky-700">{detail.ticket.pnr}</span>{detail.ticket.ticket_number ? ` · ${detail.ticket.ticket_number}` : ""}{detail.ticket.airline ? ` · ${detail.ticket.airline}` : ""}</p>}
              {detail.reference?.ref_number && <p data-testid="cb-ref-detail"><span className="text-slate-400">Reference:</span> {detail.reference.ref_type} <span className="font-semibold text-emerald-700">{detail.reference.ref_number}</span></p>}
              {detail.amount ? <p><span className="text-slate-400">Amount:</span> ₹{detail.amount}</p> : null}
              {detail.live_ride && (
                <div className="rounded-xl border border-sky-200 bg-sky-50 p-3" data-testid="cb-ride-panel">
                  {(() => {
                    const arrivedKey = `arrived:${detail.booking_id}`;
                    const waitKey = `wait:${detail.booking_id}`;
                    const showArrived = detail.arrived_at && detail.ride_status === "assigned" && !dismissedAlerts.has(arrivedKey);
                    const showWaiting = detail.waiting_metered_alerted && detail.ride_status === "assigned" && !dismissedAlerts.has(waitKey);
                    return (<>
                      {showArrived && (
                        <div data-testid="cb-arrived-banner" className="mb-2 flex items-start gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2">
                          <Car className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                          <p className="flex-1 text-[12px] font-semibold leading-snug text-emerald-800">Your driver has arrived at the pickup point. Please head over and share your pickup code.</p>
                          <button onClick={() => dismissAlert(arrivedKey)} data-testid="cb-arrived-banner-dismiss" className="shrink-0 rounded-full p-0.5 text-emerald-500 hover:bg-emerald-100"><X className="h-3.5 w-3.5" /></button>
                        </div>
                      )}
                      {showWaiting && <WaitingCostBanner detail={detail} onDismiss={() => dismissAlert(waitKey)} />}
                    </>);
                  })()}
                  {(() => {
                    const steps = [["Confirmed", true], ["Driver assigned", ["assigned", "in_progress", "completed"].includes(detail.ride_status)], ["Trip started", ["in_progress", "completed"].includes(detail.ride_status)], ["Completed", detail.ride_status === "completed"]];
                    return (
                      <div className="mb-3 flex items-center" data-testid="cb-ride-timeline">
                        {steps.map(([label, done], i) => (
                          <div key={label} className="flex flex-1 items-center last:flex-none">
                            <div className="flex flex-col items-center">
                              <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${done ? "bg-sky-600 text-white" : "bg-white text-slate-300 ring-1 ring-slate-200"}`}>{done ? "✓" : i + 1}</span>
                              <span className={`mt-1 w-16 text-center text-[9px] font-semibold leading-tight ${done ? "text-sky-700" : "text-slate-400"}`}>{label}</span>
                            </div>
                            {i < steps.length - 1 && <div className={`mx-0.5 h-0.5 flex-1 ${steps[i + 1][1] ? "bg-sky-500" : "bg-slate-200"}`} />}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  {detail.ride_status === "awaiting_assignment" && <p className="text-[13px] text-sky-700">Ride confirmed. The operator is assigning a driver — your pickup code and driver details will appear here shortly.</p>}
                  {["assigned", "in_progress"].includes(detail.ride_status) && (
                    <>
                      <div className="flex items-center gap-3">
                        {detail.driver_photo ? <img src={detail.driver_photo?.startsWith("/api") ? (process.env.REACT_APP_BACKEND_URL + detail.driver_photo) : detail.driver_photo} alt="" className="h-12 w-12 rounded-full object-cover" /> : <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-sky-500"><Ticket className="h-5 w-5" /></div>}
                        <div>
                          <p className="text-sm font-bold text-slate-800" data-testid="cb-driver-name">{detail.driver_name}</p>
                          <p className="text-xs text-slate-500">{detail.assigned_vehicle_label} · <span className="font-semibold" data-testid="cb-car-number">{detail.assigned_vehicle_reg}</span></p>
                          {detail.driver_phone && <p className="text-xs text-slate-500">{detail.driver_phone}</p>}
                        </div>
                      </div>
                      <p className="mt-2 rounded-lg bg-white px-2 py-1.5 text-[12px] font-medium text-rose-600">⚠ Before sharing your code, check the driver's photo and that the car number matches <span className="font-bold">{detail.assigned_vehicle_reg}</span>.</p>
                      {detail.ride_status === "assigned"
                        ? <p className="mt-2 text-center">Pickup code<br /><span className="text-3xl font-black tracking-[0.3em] text-slate-900" data-testid="cb-pickup-otp">{detail.pickup_otp}</span></p>
                        : <p className="mt-2 text-center text-sm font-semibold text-amber-700" data-testid="cb-ride-inprogress">Trip in progress · started at {detail.start_km} km</p>}
                      {["assigned", "in_progress"].includes(detail.ride_status) && (
                        <div className="mt-3" data-testid="cb-live-map-wrap">
                          <p className="mb-1 flex items-center gap-1 text-[11px] font-semibold text-slate-600"><MapPin className="h-3.5 w-3.5 text-sky-600" /> {detail.ride_status === "assigned" ? "Driver on the way — live location & ETA" : "Driver's live location"}</p>
                          <LiveMap bookingId={detail.booking_id} />
                          <p className="mt-1 text-[10px] text-slate-400">Updates every few seconds. Allow location access to see your distance & ETA.</p>
                          <button onClick={() => shareTrip(detail.booking_id)} data-testid="cb-share-trip" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800"><Share2 className="h-3.5 w-3.5" /> Share my live trip</button>
                        </div>
                      )}
                    </>
                  )}
                  {detail.ride_status === "completed" && <RideActions detail={detail} onRated={load} />}
                </div>
              )}
              {detail.payment_status && detail.payment_status !== "none" && <p><span className="text-slate-400">Payment:</span> {detail.payment_status}</p>}
              {detail.variant_label && <p><span className="text-slate-400">Option:</span> {detail.variant_label}</p>}
              {detail.request_summary && <p className="rounded-lg bg-sky-50 px-2 py-1 text-[13px] text-sky-700">{detail.travel_request?.subcategory_label || "Travel"}: {detail.request_summary}</p>}
              {detail.quote_note && <p className="text-violet-600">Provider note: {detail.quote_note}</p>}
              {detail.quote_flight && (
                <div className="rounded-lg border border-sky-100 bg-sky-50 p-2.5 text-[12px] text-sky-800" data-testid="cb-flight-quote-detail">
                  <p className="font-bold">{detail.quote_flight.airline || "Flight"} {detail.quote_flight.flight_no || ""}</p>
                  <p>{[detail.quote_flight.dep_date, detail.quote_flight.dep_time].filter(Boolean).join(" ")} → {[detail.quote_flight.arr_date, detail.quote_flight.arr_time].filter(Boolean).join(" ")}</p>
                  <p>{["fare_a", "fare_c", "fare_i"].map((k, ix) => detail.quote_flight[k] ? `${["Adult", "Child", "Infant"][ix]} ₹${detail.quote_flight[k]}` : null).filter(Boolean).join(" · ") || "Fare on request"}</p>
                  {detail.quote_flight.baggage && <p>Baggage: {detail.quote_flight.baggage}</p>}
                </div>
              )}
              {detail.revised_holiday && (
                <div className="rounded-lg border border-violet-100 bg-violet-50 p-2.5" data-testid="cb-revised-plan">
                  <p className="text-xs font-bold text-violet-800">Revised itinerary</p>
                  {(detail.revision_changes || []).length > 0 && <div className="mt-1 mb-1.5">{detail.revision_changes.map((c, i) => <p key={i} className="text-[11px] text-violet-700">• {c}</p>)}</div>}
                  {(detail.revised_holiday.itinerary || []).map((d, i) => (
                    <p key={i} className="mt-0.5 text-[11px] text-slate-600"><span className="font-semibold">Day {d.day || i + 1}{d.title ? ` — ${d.title}` : ""}:</span> {d.desc}{d.hotel ? ` · Stay: ${d.hotel}` : ""}</p>
                  ))}
                </div>
              )}
            </div>
            {["confirmed", "completed"].includes(detail.status) && (
              <button onClick={() => downloadEticket(detail)} data-testid="cb-download-eticket" className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-100"><Download className="h-4 w-4" /> Download e-ticket</button>
            )}
            <div className="mt-4 flex justify-end">{actions(detail)}</div>
          </div>
        </div>
      )}

      {compare && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setCompare(null)} data-testid="compare-quotes-modal">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-5" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="flex items-center gap-2 font-semibold text-slate-800"><GitCompare className="h-5 w-5 text-sky-500" /> Compare quotes</h3>
                <p className="mt-0.5 text-xs text-slate-500">{compareItems[0]?.request_summary || "Choose the agent that works best for you."}</p>
              </div>
              <button onClick={() => setCompare(null)} data-testid="compare-close" className="rounded-full p-1 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            {(() => {
              const all = compareItems.filter((b) => b.status === "quote_sent" && !isExpired(b));
              const expiredCount = compareItems.filter((b) => b.status === "quote_sent" && isExpired(b)).length;
              if (all.length === 0) return <p className="py-8 text-center text-sm text-slate-400" data-testid="compare-empty">{expiredCount ? "All quotes for this request have expired. Ask agents to re-send fresh prices." : "No quotes have arrived yet. We'll notify you when agents respond."}</p>;
              let quoted = compareMinRating > 0 ? all.filter((b) => Number(b.service_rating || 0) >= compareMinRating) : all;
              quoted = quoted.slice().sort((a, b) => compareSort === "rating" ? (Number(b.service_rating || 0) - Number(a.service_rating || 0)) : (Number(a.amount) - Number(b.amount)));
              const amounts = quoted.map((b) => Number(b.amount)).filter((n) => n > 0);
              const low = amounts.length ? Math.min(...amounts) : null;
              const high = amounts.length ? Math.max(...amounts) : null;
              const best = quoted.reduce((m, b) => (m === null || Number(b.amount) < Number(m.amount) ? b : m), null);
              const save = (low != null && high != null) ? high - low : 0;
              return (
              <>
              <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="compare-controls">
                <span className="text-xs font-semibold text-slate-500">Sort</span>
                <select value={compareSort} onChange={(e) => setCompareSort(e.target.value)} data-testid="compare-sort" className="h-8 rounded-full border border-slate-200 px-3 text-xs font-semibold text-slate-700 outline-none focus:border-fuchsia-400">
                  <option value="price">Price: low to high</option>
                  <option value="rating">Rating: high to low</option>
                </select>
                <span className="mx-1 hidden h-5 w-px bg-slate-200 sm:block" />
                {RATING_FILTERS.map(([v, l]) => (
                  <button key={v} onClick={() => setCompareMinRating(v)} data-testid={`compare-rating-${v}`} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${compareMinRating === v ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
                ))}
              </div>
              {quoted.length === 0
                ? <p className="py-8 text-center text-sm text-slate-400" data-testid="compare-filter-empty">No agents match this rating filter. Try lowering it.</p>
                : <div className="mt-3 grid gap-3 sm:grid-cols-2" data-testid="compare-list">
                {quoted.map((b) => (
                  <div key={b.booking_id} className={`rounded-2xl border p-4 ${best && b.booking_id === best.booking_id ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200"}`} data-testid={`compare-quote-${b.booking_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="line-clamp-1 text-sm font-semibold text-slate-800">{b.service_name}</p>
                      {best && b.booking_id === best.booking_id && <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[9px] font-bold text-white">Lowest</span>}
                    </div>
                    {b.service_rating_count ? <p className="mt-0.5 inline-flex items-center gap-0.5 text-xs text-amber-500"><Star className="h-3 w-3" /> {b.service_rating} <span className="text-slate-400">({b.service_rating_count})</span></p> : <p className="mt-0.5 text-xs text-slate-400">No ratings yet</p>}
                    <p className="mt-2 text-2xl font-bold text-slate-900">₹{b.amount}</p>
                    {best && b.booking_id === best.booking_id && quoted.length > 1 && save > 0 && <p className="mt-0.5 text-xs font-semibold text-emerald-600" data-testid={`compare-save-${b.booking_id}`}>Save ₹{save} vs highest</p>}
                    {b.quote_note && <p className="mt-1 line-clamp-3 text-xs text-slate-500">{b.quote_note}</p>}
                    <div className="mt-2 rounded-lg bg-slate-50 p-2 text-[11px]" data-testid={`qa-${b.booking_id}`} ref={() => markQaSeen(b)}>
                      {thread(b).map((m, i) => (
                        <p key={i} className={m.from === "provider" ? "text-emerald-700" : "text-slate-600"}><span className="font-semibold">{m.from === "provider" ? "Provider:" : "You:"}</span> {m.text}</p>
                      ))}
                      {custMsgCount(b) < 3 ? (
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <input value={askText[b.booking_id] || ""} onChange={(e) => setAskText((s) => ({ ...s, [b.booking_id]: e.target.value }))} placeholder={custMsgCount(b) ? "Ask a follow-up…" : "Ask a question…"} data-testid={`ask-input-${b.booking_id}`} className="h-8 min-w-0 flex-1 rounded-md border border-slate-200 px-2 text-[11px] outline-none" />
                          <button onClick={() => askQuote(b)} data-testid={`ask-send-${b.booking_id}`} className="shrink-0 rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-white">Ask</button>
                        </div>
                      ) : <p className="mt-1 text-slate-400">Question limit reached (3).</p>}
                    </div>
                    {b.quote_expires_at && <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-amber-600" data-testid={`compare-expiry-${b.booking_id}`}><Clock className="h-3 w-3" /> <Countdown iso={b.quote_expires_at} /></p>}
                    <button onClick={() => acceptQuote(b)} disabled={busy === b.booking_id} data-testid={`compare-accept-${b.booking_id}`} className="mt-3 inline-flex w-full items-center justify-center gap-1 rounded-full bg-violet-600 px-3 py-2 text-xs font-semibold text-white disabled:opacity-50"><Check className="h-3.5 w-3.5" /> Accept this quote</button>
                  </div>
                ))}
              </div>}
              </>
              );
            })()}
            <p className="mt-4 text-center text-[11px] text-slate-400">Accepting a quote confirms that agent and cancels the other pending requests.</p>
          </div>
        </div>
      )}

      {reviewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="review-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-5">
            <h3 className="font-semibold text-slate-800">Review {reviewing.service_name}</h3>
            <div className="mt-3 flex gap-1">{[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setRating(n)} data-testid={`star-${n}`} className={`text-2xl ${n <= rating ? "text-amber-400" : "text-slate-300"}`}>★</button>)}</div>
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience" data-testid="review-comment" className="mt-3 h-24 w-full rounded-md border border-slate-200 p-2 text-sm" />
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setReviewing(null)} className="rounded-full border border-slate-200 px-4 py-2 text-sm">Cancel</button>
              <button onClick={submitReview} data-testid="submit-review" className="rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white">Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
